import { MobileWaage } from './waage';
import { Logger } from '../helper/app-error-logger';
import { BluetoothSerial } from '@awesome-cordova-plugins/bluetooth-serial/ngx';
import { MyApp } from '../helper/app';
import { Subscription, Subject, BehaviorSubject } from 'rxjs';
import { GeraetStatusTyp, WaageKonfiguration, WaageProtokoll, WaageProtokollEintrag, WiegedatenEx } from '../model/model';
import { Utils } from '../helper/utils';
import { AppStorage } from '../helper/app-storage';
import { Wiegedaten } from '../model/swagger-model';

import moment from 'moment';
import { Util } from 'leaflet';
import { UiHelper } from '../helper/ui-helper';

const BOARDCOMPUTER = 5;
const WAAGE = 1;

const DEL = String.fromCharCode(1);
const STX = String.fromCharCode(2);
const ETX = String.fromCharCode(3);

const log = new Logger('PfreundtWaage');

/**
 * PFREUNDT Waage
 * 
 * Wird von THEURL Pellets verwendet.
 */
export class PfreundtWaage implements MobileWaage {

    konfiguration: WaageKonfiguration;
    adresse: string;
    status = new BehaviorSubject<GeraetStatusTyp>(GeraetStatusTyp.NichtVerbunden);
    dataReceiveSubscription: Subscription;
    connectSubscription: Subscription;
    aktiv: boolean;

    dagtumLetzteUhrSynchronisierung = 0;

    error = new Subject<string>();
    datenEmpfangen = new Subject<void>();
    wiegedatenEmpfangen = new Subject<Wiegedaten>();
    protokollChanged = new Subject<void>();
    letzteAlibinummerEmpfangen = new Subject<number>();

    protokoll: WaageProtokoll;

    state: PfreundtWaageState = PfreundtWaageState.Default;

    fehlerCodes = {
        'ERROR1': 'Gewicht nicht stabil',
        'ERROR2': 'Maxlast überschritten',
        'ERROR3': 'Minlast unterschritten',
        'ERROR4': 'Maximale Winkelstellung überschritten',
        'ERROR5': 'Übertragungsfehler (z.B. Funkfehler ASK, CAN Bus Fehler, etc.)',
        'ERROR7': 'Waage ist nicht bereit (z.B. Waage muss nullgestellt werden)',
        'ERROR8': 'Allgemeiner Fehler (z.B. Befehl wird in diesem Zustand nicht unterstützt)',
    }

    simulationGewicht: number = null;

    isConnected = false;

    // Interne Events
    private endeKennungEmpfangen = new Subject<void>();

    private letzteEinzelwiegungDaten: Wiegedaten = null;

    connectRunning = false;

    constructor(private bluetoothSerial: BluetoothSerial) {
        log?.info('PFREUNDT Waage');
    }

    /**
     * Setzt wie häufig die Waagen-Anbindung geprüft werden soll.
     * Wird hier nicht verwendet.
     * Es wird immer nur fährend des Wiegevorgangs kurzfristig eine Verbindung zur Waage hergestellt und anschließend direkt wirder getrennt.
     */
    setUpdateInterval(intervalMs: number) {
        // Nicht erforderlich
    }

    /**
     * Diese Methode wird alle 30 Sekunden (oder häufiger) aufgerufen, um die Verbindung zur Waage zu prüfen.
     */
    update() {
        // Wird hier nicht verwendet, da keine dauerhafte Verbindung zur Waage gehalten wird.
    }

    async info1() {
        log?.debug('info1');

        try {
            await this.connect('info1');

            // Gibt die Software-Version zurück.
            await this.senden('VERSION');
        } catch (err) {
            log.error('info1: ' + Utils.getErrorMessage(err), err);

            UiHelper.showErrorOhneSentry(err);
        } finally {
            await this.close();
        }
    }

    async info2() {
        log?.debug('info2');

        try {
            await this.connect('info2');

            // Gibt die Version des seriellen Empfangsprotokolls zurück.
            await this.senden('VERSION_PROTOCOL');
        } catch (err) {
            log.error('info2: ' + Utils.getErrorMessage(err), err);

            UiHelper.showErrorOhneSentry(err);
        } finally {
            await this.close();
        }
    }

    async info3() {
        log?.debug('RAD1');

        try {
            await this.connect('info3');

            // Gibt die Seriennummer der WK60 Montagewanne zurück. 
            await this.senden('SN');
        } catch (err) {
            log.error('info3: ' + Utils.getErrorMessage(err), err);

            UiHelper.showErrorOhneSentry(err);
        } finally {
            await this.close();
        }
    }

    stabilIndikatorAbfragen() {
        log.warn('stabilIndikatorAbfragen: Not implemented');
    }

    aufNullSetzen?() {
        log.warn('aufNullSetzen: Not implemented');
    }

    schreibeAlibieintrag?() {
        log.warn('schreibeAlibieintrag: Not implemented');
    }

    async leseLetzteAlibiNummer?() {
        log.warn('leseAlibieintrag: Not implemented');

        log?.debug('leseLetzteAlibiNummer');

        try {
            await this.connect('leseLetzteAlibiNummer');

            return new Promise<number>(async (resolve, reject) => {
                await this.senden('MOD.ALE');

                // Timeout nach 10 Sekunden
                const timeoutId = setTimeout(() => {
                    log.warn('leseLetzteAlibiNummer: Timeout nach 10 Sekunden');
                    resolve(-1);
                }, 10_000);

                const subscription = this.letzteAlibinummerEmpfangen.subscribe({
                    next: (value) => {
                        clearTimeout(timeoutId);
                        subscription.unsubscribe();
                        resolve(value);
                    },
                    error: (err) => {
                        clearTimeout(timeoutId);
                        subscription.unsubscribe();
                        resolve(-1);
                    }
                });
            });
        } catch (err) {
            log.error('leseLetzteAlibiNummer: ' + Utils.getErrorMessage(err), err);
        } finally {
            await this.close();
        }

        return 0;
    }

    waegedatenAbfragen?() {
        log.warn('waegedatenAbfragen: Not implemented');
    }

    aktiviereKontinuierlichesSendenDerWaegedaten?() {
        log.warn('aktiviereKontinuierlichesSendenDerWaegedaten: Not implemented');
    }

    async leseAnzahlGespeicherteWiegungen(): Promise<number> {
        log.warn('leseAnzahlGespeicherteWiegungen: Not implemented');
        return 0;
    }

    async getProtokoll(): Promise<WaageProtokoll> {
        if (!this.protokoll) {
            this.protokoll = await AppStorage.current.get('waage-protokoll-' + this.adresse);

            if (!this.protokoll) {
                this.protokoll = {
                    waageAdresse: this.adresse,
                    eintraege: []
                }
            }

            if (!this.protokoll.waageAdresse) {
                this.protokoll.waageAdresse = this.adresse;
            }

            if (!this.protokoll.eintraege) {
                this.protokoll.eintraege = [];
            }
        }

        return this.protokoll;
    }

    getTyp() {
        return 'pfreundt';
    }

    setActive(active: boolean) {
        if (this.aktiv === active) {
            // Keine Änderung
            return;
        }

        this.aktiv = active;

        if (active) {
            // Hier keinen Verbindung herstellen. 
            // this.connect('setActive');
        } else {
            this.close();

            this.setUpdateInterval(-1);
        }
    }

    async connect(info?: string, throwError = true): Promise<any> {
        log?.info('Connect: ' + info);

        if (!MyApp.isCordovaAvailable()) {
            // Simuliere in 50% der Fälle einen Verbindungsfehler
            await Utils.delay(500);

            if (Math.random() < 0.25) {
                // Fehler
                this.status.next(GeraetStatusTyp.NichtVerbunden);
                this.error.next('Not connected (simulation)');

                if (throwError) {
                    throw new Error('Not connected (simulation)');
                }
            } else {
                // OK
                this.status.next(GeraetStatusTyp.Verbunden);
            }

            return;
        }

        if (this.connectRunning) {
            log.warn('Connect läuft bereits. Ignoriere weiteren Connect-Aufruf.');
            return;
        }

        try {
            this.connectRunning = true;
            this.isConnected = false;

            await MyApp.ready();

            // log?.debug('Aktiviere Bluetooth...');
            // const enableResult = await this.bluetoothSerial.enable();

            // log?.debug('enableResult: ' + enableResult, enableResult);

            log?.debug('Subscribe auf Data...');
            this.dataReceiveSubscription = this.bluetoothSerial.subscribe('\n').subscribe(p => this.onDataReceived(p));

            log?.debug('Connect: ' + this.adresse);

            let errorConnecting = false;

            // Achtung: scheiss interface: Subscribe to connect, unsubscribe to disconnect.
            this.connectSubscription = this.bluetoothSerial.connect(this.adresse).subscribe({
                next: (value) => {
                    log?.debug('Connect Next: ' + value);
                    this.isConnected = true;
                },
                error: (err) => {
                    const errMessage = Utils.getErrorMessage(err);

                    log.warn('Connect Error: ' + errMessage);
                    this.status.next(GeraetStatusTyp.NichtVerbunden);
                    this.error.next('Not connected: ' + errMessage);

                    this.isConnected = false;

                    errorConnecting = true;
                },
                complete: () => {
                    log?.debug('Connect Complete');
                }
            });

            // Warte max. 20 Sekunden bis die Waage verbunden ist
            const timeout = Date.now() + 20_000;

            let verbunden = false;
            let counter = 0;

            while (Date.now() < timeout) {
                counter++;

                await Utils.delay(500);

                try {
                    if (this.isConnected) {
                        verbunden = true;
                        break;
                    }

                    if (errorConnecting) {
                        verbunden = false;
                        break;
                    }

                    await this.bluetoothSerial.isConnected();
                    log?.debug('isConnected 2: TRUE');
                    verbunden = true;
                    break;
                } catch (err) {
                    // Nicht verbunden
                    log?.debug('isConnected 2 (Counter: ' + counter + '): ' + Utils.getErrorMessage(err));
                }
            }

            if (!verbunden) {
                throw new Error('Not connected (timeout)');
            }

            this.status.next(GeraetStatusTyp.Verbunden);
        } catch (err) {
            log.warn(`Connect Waage (${this.adresse}): ` + Utils.getErrorMessage(err), err);
            this.status.next(GeraetStatusTyp.NichtVerbunden);

            let errMessage = Utils.getErrorMessage(err);

            if (errMessage.indexOf('User did not enable Bluetooth') >= 0) {
                errMessage = 'Bluetooth nicht aktiv';
            }

            this.error.next(errMessage);

            if (throwError) {
                throw err;
            }
        } finally {
            this.connectRunning = false;

            log?.debug('connect done: ' + this.isConnected);
        }
    }

    async close(): Promise<any> {
        log?.debug('Close');

        this.status.next(GeraetStatusTyp.NichtVerbunden);

        if (!MyApp.isCordovaAvailable()) {
            return;
        }

        try {
            if (this.connectSubscription) {
                this.connectSubscription.unsubscribe();
                this.connectSubscription = null;
            }
        } catch (err) {
            log.warn(`Close Waage (connectSubscription): ` + Utils.getErrorMessage(err));
        }

        try {
            if (this.dataReceiveSubscription) {
                this.dataReceiveSubscription.unsubscribe();
                this.dataReceiveSubscription = null;
            }
        } catch (err) {
            log.warn(`Close Waage (dataReceiveSubscription): ` + Utils.getErrorMessage(err));
        }

        try {
            log?.debug('Disconnecting bluetooth...');
            await this.bluetoothSerial.disconnect();
            log?.debug('Disconnected');
        } catch (err) {
            this.error.next(Utils.getErrorMessage(err));
            log.warn(`Close Waage: ${err.message}`);
        }

        this.isConnected = false;
    }

    async dispose(): Promise<void> {
        log?.debug('dispose');

        this.aktiv = false;

        await this.close();

        Utils.removeAllSubscribers(
            this.error,
            this.datenEmpfangen,
            this.wiegedatenEmpfangen,
            this.protokollChanged,
            this.letzteAlibinummerEmpfangen
        );
    }

    async pruefeVerbindung(): Promise<void> {
        // log?.debug('pruefeVerbindung');

        // if (!this.aktiv) {
        //     this.status.next(GeraetStatusTyp.NichtVerbunden);
        //     return;
        // }

        // if (!MyApp.isCordovaAvailable()) {
        //     return;
        // }

        // let connected = false;

        // try {
        //     await this.bluetoothSerial.isConnected();

        //     connected = true;

        //     log?.debug('isConnected: TRUE');
        //     this.status.next(GeraetStatusTyp.Verbunden);

        // } catch (err) {
        //     // Nicht verbunden
        //     log?.debug('isConnected: ' + Utils.getErrorMessage(err), err);
        //     this.status.next(GeraetStatusTyp.NichtVerbunden);
        // }

        // try {
        //     if (connected) {
        //         // Die Uhr soll derzeit bei THEURL nicht automatisch synchronisiert werden. Ist noch nicht abgestimmt.
        //         // const maxAlter = 12 * 60 * 60 * 1000;

        //         // if (this.dagtumLetzteUhrSynchronisierung < Date.now() - maxAlter) {
        //         //     this.dagtumLetzteUhrSynchronisierung = Date.now();

        //         //     this.syncUhr(false);
        //         // }
        //     } else {
        //         log?.debug('Waage nicht verbunden. Versuche reconnect...');

        //         await this.close();
        //         await this.connect('pruefeVerbindung');
        //     }
        // } catch (err) {
        //     log.warn('FEHLER pruefeVerbindung: ' + Utils.getErrorMessage(err), err);
        //     this.error.next(Utils.getErrorMessage(err));
        //     this.status.next(GeraetStatusTyp.Fehler);
        // }
    }

    async wiegen(nr: number): Promise<boolean> {
        log?.debug('wiegen: ' + nr);

        try {
            await this.connect('wiegen');

            if (nr == 1) {
                // Erste Wiegung
                await this.senden('WE');
            } else if (nr == 2) {
                // Zweite Wiegung
                await this.senden('WZ');
            }

            // TODO: Warte auf Antwort
            await Utils.delay(2000);

            return true;
        } catch (err) {
            log.error('wiegen: ' + Utils.getErrorMessage(err), err);
            return false;
        } finally {
            await this.close();
        }
    }

    /**
     * FÜhrt eine Einzelwiegung durch.
     */
    async einzelwiegung(): Promise<WiegedatenEx> {
        log?.debug('einzelwiegung');

        try {
            await this.connect('einzelwiegung');

            this.letzteEinzelwiegungDaten = null;

            await this.senden('T1');

            // Warte auf das Event endeKennungEmpfangen oder Timeout
            await new Promise<void>(async (resolve) => {
                const subscription = this.endeKennungEmpfangen.subscribe({
                    next: () => {
                        cleanup();
                        resolve();
                    },
                    error: (err2: any) => {
                        log.error('einzelwiegung: Error im Subscription: ' + Utils.getErrorMessage(err2), err2);
                        cleanup();
                        resolve();
                    }
                });

                const timeoutId = setTimeout(() => {
                    log.warn('einzelwiegung: Timeout nach 30 Sekunden');
                    cleanup();
                    resolve();
                }, 30000);

                const cleanup = () => {
                    subscription.unsubscribe();
                    clearTimeout(timeoutId);
                };

                if (!MyApp.isCordovaAvailable()) {
                    // Simuliere Antworten von der Waage
                    if (!this.simulationGewicht) {
                        this.simulationGewicht = 1000;
                    }

                    await Utils.delay(250);
                    await this.onDataReceived(STX + 'T1' + ETX);

                    // OK, sobald die Wiegung erfolgreich war
                    await Utils.delay(1000);
                    await this.onDataReceived(STX + 'OK' + ETX);

                    // Liste mit Wiegungen senden
                    await Utils.delay(100);
                    await this.onDataReceived(STX + '20250923' + DEL + '083346' + DEL + '1' + DEL + this.simulationGewicht + DEL + '0.000' + DEL + '0.000' + DEL + 'W1' + DEL + '63405' + ETX);

                    // Ende-Kennung
                    await Utils.delay(100);
                    await this.onDataReceived(STX + 'E' + ETX);
                }
            });

            return this.letzteEinzelwiegungDaten;
        } catch (err) {
            log.error('einzelwiegung: ' + Utils.getErrorMessage(err), err);

            return {
                errorMessage: Utils.getErrorMessage(err)
            };
        } finally {
            await this.close();
        }
    }

    async aktiviereWiegemodus(): Promise<boolean> {
        // Wird nicht verwendet.
        return true;
    }

    async tarieren() {
        log?.debug('tarieren');

        try {
            await this.connect('tarieren');
            await this.senden('1#101#5#1#');
        } catch (err) {
            log.error('tarieren: ' + Utils.getErrorMessage(err), err);
        } finally {
            await this.close();
        }
    }

    speichereProtokoll() {
        // Zu alte Einträge löschen
        const maxEintraege = 100;
        const protokoll = this.protokoll;

        if (protokoll.eintraege.length > maxEintraege) {
            protokoll.eintraege.splice(0, protokoll.eintraege.length - maxEintraege);
        }

        AppStorage.current.set('waage-protokoll-' + this.adresse, protokoll);

        this.protokollChanged.next();
    }

    async onDataReceived(message: string) {
        try {
            log?.debug('Waage onDataReceived: ' + message);

            this.status.next(GeraetStatusTyp.Verbunden);
            this.datenEmpfangen.next();

            if (!message) {
                return;
            }

            const protokoll = await this.getProtokoll();

            const protokollEintrag: WaageProtokollEintrag = {
                timestamp: Date.now(),
                rohdaten: message,
                typ: 'unbekannt',
                daten: ''
            };

            protokoll.eintraege.push(protokollEintrag);

            this.speichereProtokoll();

            // Suche STX
            const posSTX = message.indexOf(STX);

            if (posSTX < 0) {
                // Kein STX gefunden. Ignorieren
                log.warn('Keine STX in Nachricht gefunden');
                return;
            }

            // Suche ETX
            const posETX = message.lastIndexOf(ETX);

            if (posETX < 0) {
                // Kein ETX gefunden. Ignorieren
                log.warn('Keine ETX in Nachricht gefunden');
                return;
            }

            const nutzdaten = message.substring(posSTX + 1, posETX);

            // Wiededaten Beispiel:
            // {5#30#1#1915486214#08#02#20#12#57#50##VIE-EN xxx#####110##110##0#######@99}

            log?.debug('Nutzdaten: ' + nutzdaten);

            // Entscheide anhand der ersten zwei Zeichen um was für eine Nachricht es sich handelt
            const nachrichtTyp = nutzdaten.substring(0, 2).trim();

            // Handelt es sich um Wiegedaten?

            switch (nachrichtTyp) {
                case 'T1':
                    // Die Waage führt jetzt die Einzelwiegung durch
                    log?.debug('Einzelwiegung gestartet: T1');
                    this.state = PfreundtWaageState.Einzelwiegung;
                    protokollEintrag.daten = 'Einzelwiegung';
                    protokollEintrag.typ = 'T1';
                    break;

                case 'WE':
                    // Die Waage führt jetzt die Erstwiegung durch
                    log?.debug('Erstwiegung gestartet: WE');
                    this.state = PfreundtWaageState.Erstwiegung;
                    protokollEintrag.daten = 'Erstwiegung';
                    protokollEintrag.typ = 'WE';
                    break;

                case 'WZ':
                    // Die Waage führt jetzt die Zweitwiegung durch und sendet anschließend die Wiegedaten
                    log?.debug('Zweitwiegung gestartet: WZ');
                    this.state = PfreundtWaageState.Zweitwiegung;
                    protokollEintrag.daten = 'Zweitwiegung';
                    protokollEintrag.typ = 'WZ';
                    break;

                case 'OK':
                    // Die Erst- oder Zweitwiegung war erfolgreich. Anschließend werden die Wiegedaten gesendet.
                    log?.debug('Wiegung erfolgreich: OK');
                    protokollEintrag.daten = '';
                    protokollEintrag.typ = 'OK';
                    break;

                case 'E':
                    log?.debug('Ende-Kennung empfangen. Bestätige den Erhalt mit ACK.');
                    // this.state = PfreundtWaageState.Default;
                    await this.senden('ACK');
                    protokollEintrag.daten = 'Ende-Kennung';
                    protokollEintrag.typ = 'E';

                    // Löse das Event aus, damit die Einzelwiegung-Methode fortfahren kann.
                    this.endeKennungEmpfangen.next();
                    break;

                case 'ER':
                    const fehlermeldung = this.fehlerCodes[nutzdaten] || 'Unbekannter Fehler';
                    log.warn('Waage Fehler: ' + fehlermeldung);
                    protokollEintrag.daten = fehlermeldung;
                    protokollEintrag.typ = 'ERROR';
                    break;

                case '20':
                case '21':
                    // Wiegedaten
                    // Es wird hier auf 20 bzw. 21, da Wiegedaten immer mit dem Jahr beginnen (z.B. 20250124...)
                    // 20 = 2000-2099
                    // 21 = 2100-2199 (so lange wird es RCM und PFREUNDT wahrscheinlich nicht geben, aber wer weiß...)
                    this.verarbeiteWiegedaten(nutzdaten, protokollEintrag);
                    break;

                default:
                    log.warn('Unbekannte Nutzdaten für Default-State: ' + nutzdaten);
                    break;
            }

        } catch (err) {
            log.error('onDataReceived', err);
            this.error.next(Utils.getErrorMessage(err));
        }
    }

    async processMessage(message: string) {
        try {

        } catch (err) {
            log.error('onDataReceived', err);
            this.error.next(Utils.getErrorMessage(err));
        }
    }

    verarbeiteWiegedaten(nutzdaten: string, protokollEintrag: WaageProtokollEintrag) {
        log?.debug('verarbeiteWiegedaten: ' + nutzdaten);
        const data = nutzdaten.split(DEL);

        let idx = 0;

        const datum = data[idx++];
        const uhrzeit = data[idx++];
        const bonnr = Utils.parseInt(data[idx++]);
        const brutto = Utils.parseFloat(data[idx++]);
        const tara = Utils.parseFloat(data[idx++]);
        const netto = Utils.parseFloat(data[idx++]);
        const kennung = data[idx++];
        const seriennr = data[idx++];

        const m = moment(datum + uhrzeit, 'YYYYMMDDHHmmss');

        const wiegedaten: Wiegedaten = {
            Wiegenummer: bonnr,
            Datum: m.toISOString(),
            Wiegung1: tara,
            Wiegung2: brutto,
            Netto: netto
        };

        if (this.state === PfreundtWaageState.Einzelwiegung) {
            wiegedaten.Wiegung1 = brutto;
            wiegedaten.Wiegung2 = 0;
            wiegedaten.Netto = 0;
        }

        log?.info('Wiegedaten gelesen: ' + JSON.stringify(wiegedaten));

        if (this.state === PfreundtWaageState.Einzelwiegung) {
            // Merke die letzten empfangenen Wiegedaten für die Einzelverwiegen-Methode
            this.letzteEinzelwiegungDaten = wiegedaten;
        } else {
            // Event auslösen und damit die Wiegung speichern.
            // Das ist bei der Einzelverwiegen-Methode nicht gewünscht. Hier wird das Ergebnis direkt von der Methode zurückgegeben und muss nicht später zugeordnet werden.
            this.wiegedatenEmpfangen.next(wiegedaten);
        }

        // Antworten
        // this.senden(`1#31#5#${serialNumber}#`);

        protokollEintrag.daten = `Nr. ${bonnr}, Datum: ${m.format('DD.MM.YYYY HH:mm:ss')}, Brutto: ${brutto}, Netto: ${netto}, Tara: ${tara}`;
        protokollEintrag.typ = 'wiegung';

        this.speichereProtokoll();
    }

    async syncUhr(pruefeVerbindung = true) {
        log?.debug('syncUhr');

        try {
            if (pruefeVerbindung) {
                this.pruefeVerbindung();
            }

            const now = moment();

            // Format: yyyymmddHHMMSS
            // SET_DATETIME;20121115153100
            const daten = 'SET_DATETIME;' + now.format('YYYYMMDDHHmmss');

            await this.senden(daten);
        } catch (err) {
            log.error('syncUhr: ' + Utils.getErrorMessage(err), err);
        }
    }

    async senden(nutzdaten: string) {
        // Nachrichtenaufbau: STX + Nutzdaten + ETX + 0x0D 0x0A
        const message = STX + nutzdaten + ETX + '\r\n';

        log?.debug('senden: ' + message);

        const protokoll = await this.getProtokoll();

        protokoll.eintraege.push({
            daten: 'SEND: ' + message,
            typ: 'senden',
            timestamp: Date.now(),
            rohdaten: message
        });

        this.bluetoothSerial.write(message);
    }

    /**
     * Wandelt eine Zahl in einen 4-Byte ArrayBuffer um.
     * siehe: https://stackoverflow.com/questions/15761790/convert-a-32bit-integer-into-4-bytes-of-data-in-javascript
     */
    toBytesInt32(num: number): ArrayBuffer {
        const arr = new ArrayBuffer(4); // an Int32 takes 4 bytes
        const view = new DataView(arr);
        view.setUint32(0, num, false); // byteOffset = 0; litteEndian = false
        return arr;
    }
}

enum PfreundtWaageState {
    Default,
    Erstwiegung,
    Zweitwiegung,
    Einzelwiegung
}